import { StyleSheet, css } from 'aphrodite';
import { FC, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import { IconMic, IconSend } from './icons';

/**
 * Props for {@link Composer}. See `ui/README.md` for the interaction contract
 * (Enter to send, Shift+Enter for newline, dictation mic).
 */
export type ComposerProps = {
	/** Called when the user submits a non-empty message. */
	onSend: (text: string) => void;
	/** Disable the textarea and send button (e.g. while the AI is responding). */
	disabled?: boolean;
	/** Optional textarea placeholder. Defaults to "Ask about this lead…". */
	placeholder?: string;
};

/**
 * Browser SpeechRecognition shape — narrowed locally so we don't rely on
 * `lib.dom`'s incomplete typings or pull a third-party d.ts. Mirrors the
 * subset used by the components-v2 `PromptInputSpeechButton`.
 */
type BrowserSpeechRecognition = EventTarget & {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	start(): void;
	stop(): void;
	onstart: ((this: BrowserSpeechRecognition, ev: Event) => unknown) | null;
	onend: ((this: BrowserSpeechRecognition, ev: Event) => unknown) | null;
	// `onresult` callback signature from the WebSpeech API. We only read
	// `results[i].isFinal` and `results[i][0].transcript` so the loose typing
	// below is sufficient.
	onresult:
		| ((
				this: BrowserSpeechRecognition,
				ev: Event & {
					resultIndex: number;
					results: ArrayLike<{ isFinal: boolean; [index: number]: { transcript: string } }>;
				}
		  ) => unknown)
		| null;
	onerror: ((this: BrowserSpeechRecognition, ev: Event & { error: string }) => unknown) | null;
};

type SpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

const getSpeechRecognitionCtor = (): SpeechRecognitionConstructor | null => {
	if (typeof window === 'undefined') return null;
	const w = window as unknown as {
		SpeechRecognition?: SpeechRecognitionConstructor;
		webkitSpeechRecognition?: SpeechRecognitionConstructor;
	};
	return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

/**
 * Composer sitting above the message thread. Textarea + dictation mic +
 * send button.
 *
 * - Enter submits and prevents default; Shift+Enter inserts a newline.
 * - Dictation: clicking the mic toggles a Web Speech API session and
 *   appends final transcripts into the textarea (mirrors the components-v2
 *   `PromptInputSpeechButton` behavior). When the browser does not support
 *   speech recognition, the mic is disabled with a "Dictation not supported"
 *   tooltip rather than hidden — matches the legacy fallback.
 * - Attach/file uploads are deliberately **not** rendered: the legacy
 *   queue-note flow doesn't support attachments either, so we don't claim a
 *   capability we don't ship.
 * - Send is disabled when the trimmed text is empty or `disabled` is true.
 * - The textarea clears on successful send.
 */
export const Composer: FC<ComposerProps> = ({ onSend, disabled = false, placeholder = 'Ask about this lead…' }) => {
	const [text, setText] = useState('');
	const [isListening, setIsListening] = useState(false);
	// Track support as state so the disabled/title flips correctly after the
	// effect resolves on mount (the ref is null on first render).
	const [speechSupported, setSpeechSupported] = useState<boolean>(() => getSpeechRecognitionCtor() !== null);
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
	const trimmed = text.trim();
	const canSend = trimmed.length > 0 && !disabled;

	const send = () => {
		if (!canSend) return;
		onSend(trimmed);
		setText('');
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			send();
		}
	};

	// Lazily construct the SpeechRecognition instance once on mount. We pin a
	// stable ref so the click handler can stop in-flight sessions on unmount
	// without re-running the effect on every state change.
	useEffect(() => {
		const Ctor = getSpeechRecognitionCtor();
		if (!Ctor) {
			setSpeechSupported(false);
			return;
		}
		setSpeechSupported(true);
		const recognition = new Ctor();
		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.lang = 'en-US';
		recognition.onstart = () => setIsListening(true);
		recognition.onend = () => setIsListening(false);
		recognition.onerror = () => setIsListening(false);
		recognition.onresult = event => {
			let finalTranscript = '';
			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i];
				if (result.isFinal) {
					finalTranscript += result[0]?.transcript ?? '';
				}
			}
			if (!finalTranscript) return;
			// Append to whatever is in the controlled `text` state — using the
			// functional setter so we don't capture a stale closure if the user
			// keeps typing while dictation is running.
			setText(prev => (prev ? `${prev} ${finalTranscript}` : finalTranscript));
		};
		recognitionRef.current = recognition;
		return () => {
			recognition.stop();
			recognitionRef.current = null;
		};
	}, []);

	const toggleDictation = useCallback(() => {
		const recognition = recognitionRef.current;
		if (!recognition) return;
		if (isListening) {
			recognition.stop();
		} else {
			recognition.start();
		}
	}, [isListening]);

	const dictateTitle = !speechSupported
		? 'Dictation not supported in this browser'
		: isListening
			? 'Stop dictation'
			: 'Dictate';

	return (
		<div className={css(styles.wrap)}>
			<div className={css(styles.shell)}>
				<textarea
					ref={textareaRef}
					className={css(styles.textarea)}
					value={text}
					onChange={e => setText(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					rows={1}
					disabled={disabled}
				/>
				<div className={css(styles.row)}>
					<button
						type='button'
						onClick={toggleDictation}
						disabled={!speechSupported || disabled}
						className={css(
							styles.iconBtn,
							!speechSupported || disabled ? styles.iconBtnDisabled : styles.iconBtnIdle,
							isListening && styles.iconBtnActive
						)}
						aria-label='Dictate'
						aria-pressed={isListening}
						title={dictateTitle}
					>
						<IconMic size={16} sw={1.8} />
					</button>
					<span className={css(styles.spacer)} />
					<span className={css(styles.hint)}>⏎ to send</span>
					<button
						type='button'
						onClick={send}
						disabled={!canSend}
						className={css(styles.sendBtn, canSend ? styles.sendBtnActive : styles.sendBtnIdle)}
						aria-label='Send message'
					>
						<IconSend size={14} sw={2} />
					</button>
				</div>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	wrap: {
		padding: '20px 24px 16px',
	},
	shell: {
		border: '1px solid var(--border)',
		borderRadius: 14,
		background: 'var(--bg-elev)',
		padding: 12,
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		boxShadow: 'var(--shadow-sm)',
	},
	textarea: {
		width: '100%',
		border: 'none',
		outline: 'none',
		background: 'transparent',
		resize: 'none',
		minHeight: 24,
		maxHeight: 120,
		fontSize: 14,
		lineHeight: 1.5,
		color: 'var(--ink)',
		fontFamily: 'inherit',
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
	},
	spacer: {
		flex: 1,
	},
	hint: {
		fontSize: 11,
		color: 'var(--ink-faint)',
		fontFamily: 'var(--font-mono)',
	},
	iconBtn: {
		width: 32,
		height: 32,
		borderRadius: 8,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		border: 'none',
		transition: 'background 120ms, color 120ms',
		padding: 0,
	},
	iconBtnIdle: {
		background: 'transparent',
		color: 'var(--ink-muted)',
		cursor: 'pointer',
		':hover': {
			background: 'var(--bg-sunken)',
			color: 'var(--ink)',
		},
	},
	iconBtnActive: {
		background: 'var(--bg-sunken)',
		color: 'var(--ink)',
	},
	iconBtnDisabled: {
		background: 'transparent',
		color: 'var(--ink-faint)',
		cursor: 'not-allowed',
		opacity: 0.6,
	},
	sendBtn: {
		width: 32,
		height: 32,
		borderRadius: 8,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		border: 'none',
		transition: 'background 120ms',
	},
	sendBtnActive: {
		background: 'var(--ink)',
		color: 'var(--bg)',
		cursor: 'pointer',
	},
	sendBtnIdle: {
		background: 'var(--bg-sunken)',
		color: 'var(--ink-faint)',
		cursor: 'not-allowed',
	},
});
